import { useState, useEffect, useCallback } from 'react';
import { polygonMarketDataService, PolygonQuote } from '@services/api/polygonMarketData.service';

export interface MarketData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
}

interface UseMarketDataReturn {
  marketData: MarketData[];
  isLoading: boolean;
  error: string | null;
  isMarketOpen: boolean;
  marketStatus: string;
  refreshData: () => Promise<void>;
  lastUpdated: Date | null;
}

// Default tickers to display on HomeScreen
// Note: Indexes are fetched separately via stockDataService (Polygon doesn't support indexes)
const DEFAULT_TICKERS = ['AAPL', 'NVDA', 'TSLA', 'MSFT', 'GOOGL', 'AMZN', 'META'];

export const useMarketData = (tickers: string[] = DEFAULT_TICKERS): UseMarketDataReturn => {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const isMarketOpen = polygonMarketDataService.isMarketOpen();
  const marketStatus = polygonMarketDataService.getMarketStatus();

  const fetchMarketData = useCallback(
    async (useCache: boolean = true) => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await polygonMarketDataService.getBatchSnapshot(
          tickers,
          useCache
        );

        if (fetchError || !data) {
          throw new Error(fetchError || 'Failed to fetch market data');
        }

        // Transform Polygon data to MarketData format
        const transformedData: MarketData[] = data.map((quote: PolygonQuote) => ({
          symbol: quote.ticker,
          name: quote.name || quote.ticker,
          price: quote.price,
          change: quote.change,
          changePercent: quote.changePercent,
        }));

        setMarketData(transformedData);
        setLastUpdated(new Date());
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('useMarketData error:', errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [tickers.join(',')] // Use join to avoid array reference comparison issues
  );

  // Refresh data (force bypass cache)
  const refreshData = useCallback(async () => {
    await fetchMarketData(false);
  }, [fetchMarketData]);

  // Initial fetch on mount
  useEffect(() => {
    fetchMarketData(true);
  }, [fetchMarketData]);

  // Auto-refresh every 5 minutes during market hours
  useEffect(() => {
    if (!isMarketOpen) return;

    const interval = setInterval(
      () => {
        fetchMarketData(true);
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [isMarketOpen, fetchMarketData]);

  return {
    marketData,
    isLoading,
    error,
    isMarketOpen,
    marketStatus,
    refreshData,
    lastUpdated,
  };
};
